import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils/common';
import { isScrollable, canScrollTo } from '@flex-design-system/fx-utils/element';
import { useSize, useResizeObserver } from '@flex-design-system/fx-utils/hooks';
import { CloseIcon } from '@flex-design-system/legacy-flex-icons';
import { useMergedCSS } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { forwardRef, useMemo, useState, useRef, useCallback, useLayoutEffect } from 'react';
import usePopperPresence from '../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../hooks/usePreventTransitionInteraction.mjs';
import { getColor } from '../../utils/colors.mjs';
import { overlayStyle } from '../../utils/css.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';
import InjectAside from '../utility/InjectAside.mjs';
import { ModalAside, ModalAsideScrollArea, StyledAsideFooter } from './components/Aside/ModalAside.mjs';
import ModalContent_Legacy from './components/legacy/Content_Legacy.mjs';
import ModalScrollArea_Legacy from './components/legacy/ScrollArea_Legacy.mjs';
import { StyledFooter_Legacy } from './components/legacy/styled.mjs';
import { useModalContext, ModalProvider } from './context.mjs';
import { Footer } from './Footer.mjs';
import useModalContentElement from './hooks/useModalContentElement.mjs';
import { useModalFooterElement } from './hooks/useModalFooterElement.mjs';
import { StyledOverlay, StyledContentPositioner, StyledContent, ContentRoot, FOG_HEIGHT, StyledScrollAreaRoot, StyledScrollAreaViewport, StyledScrollAreaChildrenRoot, StyledFog, StyledTitle, StyledCloseIconButtonRoot, StyledPortalContainer, StyledHeader, StyledDescription, StyledSection, StyledBody } from './styled.mjs';
import Toolbar, { ToolbarTitle } from './Toolbar.mjs';

const ModalRoot = (props) => {
  const [scrollPos, setScrollPos] = useState(0);
  const [title, setTitle] = useState(null);
  const [toolbar, setToolbar] = useState(null);
  const [contentElement, setContentElement] = useState(null);
  const [modalContentElement, setModalContentElement] = useState(null);
  const [modalFooterElement, setModalFooterElement] = useState(null);
  const [portalContainerElement, setPortalContainerElement] = useState(void 0);
  const [hasLeftAsideChildren, setHasLeftAsideChildren] = useState(false);
  const [hasRightAsideChildren, setHasRightAsideChildren] = useState(false);
  const {
    children,
    open: openProp,
    defaultOpen,
    onOpenChange,
    onClose,
    ...rootProps
  } = props;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const handleClose = useCallbackRef(onClose);
  return /* @__PURE__ */ jsx(
    ModalProvider,
    {
      modal: rootProps.modal,
      open,
      scrollPos,
      setScrollPos,
      title,
      onTitleChange: setTitle,
      toolbar,
      onToolbarChange: setToolbar,
      contentElement,
      onContentElementChange: setContentElement,
      onClose: handleClose,
      hasLeftAsideChildren,
      onHasLeftAsideChildrenChange: setHasLeftAsideChildren,
      hasRightAsideChildren,
      onHasRightAsideChildrenChange: setHasRightAsideChildren,
      portalContainerElement,
      onPortalContainerElementChange: setPortalContainerElement,
      modalContentElement,
      onChangeModalContentElement: setModalContentElement,
      modalFooterElement,
      onChangeModalFooterElement: setModalFooterElement,
      children: /* @__PURE__ */ jsx(DialogPrimitive.Root, { open, onOpenChange: setOpen, ...rootProps, children })
    }
  );
};
const ModalContent = forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      leftAside,
      rightAside,
      placement = "center",
      overlayTheme = "light",
      positionerCSS,
      animation = "default",
      css: cssFromProps,
      style: styleFromProps,
      portalContainer: portalContainerFromProps,
      zIndex,
      initialFocusElement,
      onOpenAutoFocus: onOpenAutoFocusFromProps,
      ...restProps
    } = props;
    const {
      modal,
      open,
      onClose,
      hasLeftAsideChildren,
      hasRightAsideChildren,
      onChangeModalContentElement,
      portalContainerElement: portalContainerElementFromContext,
      setScrollPos
    } = useModalContext("Modal.Content");
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => {
        if (isPresent) {
          return;
        }
        onClose?.();
        setScrollPos(0);
      }
    });
    const composedRef = useComposedRefs(
      forwardedRef,
      presenceRef,
      onChangeModalContentElement
    );
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    const portalContainer = portalContainerFromProps ?? portalContainerElementFromContext;
    const positionStyle = useMemo(() => {
      if (isNil(portalContainer)) {
        return;
      }
      return { position: "absolute" };
    }, [portalContainer]);
    const handleOpenAutoFocus = composeEventHandlers(
      onOpenAutoFocusFromProps,
      (event) => {
        const restrictedFocusElement = typeof initialFocusElement === "function" ? initialFocusElement() : initialFocusElement;
        if (!isNil(restrictedFocusElement)) {
          restrictedFocusElement.focus();
          event.preventDefault();
        }
      }
    );
    return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { container: portalContainer, children: [
      /* @__PURE__ */ jsx(
        StyledOverlay,
        {
          className: overlayStyle({ overlayTheme }),
          css: useMergedCSS(positionStyle, zIndexStyle)
        }
      ),
      /* @__PURE__ */ jsx(
        StyledContentPositioner,
        {
          placement,
          modal,
          open,
          css: useMergedCSS(positionStyle, positionerCSS, zIndexStyle),
          children: /* @__PURE__ */ jsxs(
            StyledContent,
            {
              css: cssFromProps,
              style: { ...styleFromProps, ...preventTransitionInteraction.style },
              ref: composedRef,
              animation,
              onOpenAutoFocus: handleOpenAutoFocus,
              ...restProps,
              onKeyDownCapture: (e) => {
                preventTransitionInteraction.onKeyDownCapture?.(e);
                restProps.onKeyDownCapture?.(e);
              },
              children: [
                leftAside ? /* @__PURE__ */ jsx(InjectAside, { side: "left", children: leftAside }) : null,
                /* @__PURE__ */ jsx(
                  ContentRoot,
                  {
                    hasAsideChild: hasLeftAsideChildren || hasRightAsideChildren,
                    children
                  }
                ),
                rightAside ? /* @__PURE__ */ jsx(InjectAside, { side: "right", children: rightAside }) : null
              ]
            }
          )
        }
      )
    ] });
  }
);
const ModalLeftAsideRoot = ({ children }) => {
  const { onHasLeftAsideChildrenChange } = useModalContext("ModalLeftAsideRoot");
  useLayoutEffect(() => {
    onHasLeftAsideChildrenChange(true);
    return () => {
      onHasLeftAsideChildrenChange(false);
    };
  }, [onHasLeftAsideChildrenChange]);
  return /* @__PURE__ */ jsx(InjectAside, { side: "left", children });
};
const ModalRightAsideRoot = ({
  children
}) => {
  const { onHasRightAsideChildrenChange } = useModalContext(
    "ModalRightAsideRoot"
  );
  useLayoutEffect(() => {
    onHasRightAsideChildrenChange(true);
    return () => {
      onHasRightAsideChildrenChange(false);
    };
  }, [onHasRightAsideChildrenChange]);
  return /* @__PURE__ */ jsx(InjectAside, { side: "right", children });
};
const ModalScrollArea = forwardRef((props, ref) => {
  const {
    children,
    css: cssFromProps,
    className,
    toolbar,
    onScroll,
    toolbarHeightFallback = 0,
    bottomFog,
    ...bodyProps
  } = props;
  const [viewportElement, setViewportElement] = useState(
    null
  );
  const childrenRef = useRef(null);
  const {
    onContentElementChange,
    setScrollPos,
    toolbar: toolbarElement
  } = useModalContext("Modal.ScrollArea");
  const childrenComposedRef = useComposedRefs(
    childrenRef,
    onContentElementChange
  );
  const scrollable = isScrollable(viewportElement);
  const isFogDisabled = bottomFog === false;
  const isFogDisplayed = !isFogDisabled && canScrollTo(viewportElement, "bottom");
  const toolbarSize = useSize(toolbarElement ?? null);
  const toolbarElementHeight = toolbarSize?.height ?? 0;
  const toolbarHeight = toolbarElementHeight > 0 ? toolbarElementHeight : toolbarHeightFallback;
  const css = useMergedCSS(cssFromProps, {
    paddingBottom: scrollable ? FOG_HEIGHT : 64,
    paddingTop: toolbarHeight
  });
  const handleScroll = useCallback(() => {
    setScrollPos(viewportElement?.scrollTop ?? 0);
  }, [setScrollPos, viewportElement?.scrollTop]);
  useResizeObserver(childrenRef.current, handleScroll);
  return /* @__PURE__ */ jsx(
    StyledScrollAreaRoot,
    {
      ref,
      css: {
        [`& ${ScrollArea.Scrollbar}`]: {
          marginTop: toolbarHeight
        }
      },
      ...bodyProps,
      children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
        /* @__PURE__ */ jsxs(
          StyledScrollAreaViewport,
          {
            css,
            className,
            ref: setViewportElement,
            onScroll: composeEventHandlers(onScroll, handleScroll),
            children: [
              toolbar,
              /* @__PURE__ */ jsx(StyledScrollAreaChildrenRoot, { ref: childrenComposedRef, children })
            ]
          }
        ),
        isFogDisplayed ? bottomFog ?? /* @__PURE__ */ jsx(StyledFog, { side: "bottom", color: "white" }) : null
      ] })
    }
  );
});
const ModalTitle = forwardRef((props, ref) => {
  const { onTitleChange } = useModalContext("ModalHeader");
  const composedRef = useComposedRefs(ref, onTitleChange);
  return /* @__PURE__ */ jsx(StyledTitle, { ...props, ref: composedRef });
});
const ModalCloseIconButton = forwardRef(({ icon, ...props }, ref) => {
  return /* @__PURE__ */ jsx(DialogPrimitive.Close, { asChild: true, children: /* @__PURE__ */ jsx(
    StyledCloseIconButtonRoot,
    {
      clickableStyle: props.clickableStyle ?? "none",
      ref,
      ...props,
      children: icon ? icon : /* @__PURE__ */ jsx(CloseIcon, { size: 26, color: "blackAlpha012" })
    }
  ) });
});
const ModalPortalContainer = forwardRef((props, ref) => {
  const { onPortalContainerElementChange } = useModalContext(
    "Modal.PortalContainer"
  );
  const composedRef = useComposedRefs(ref, onPortalContainerElementChange);
  return /* @__PURE__ */ jsx(StyledPortalContainer, { ...props, ref: composedRef });
});
const ModalFog = forwardRef(
  ({ color: colorFromProps = "white", ...props }, ref) => {
    const color = getColor(colorFromProps);
    return /* @__PURE__ */ jsx(StyledFog, { side: "bottom", color, ...props, ref });
  }
);
const Modal = ModalRoot;
Modal.Content = ModalContent;
Modal.Content_Legacy = ModalContent_Legacy;
Modal.Trigger = DialogPrimitive.Trigger;
Modal.Close = DialogPrimitive.Close;
Modal.CloseIconButton = ModalCloseIconButton;
Modal.Toolbar = Toolbar;
Modal.ToolbarTitle = ToolbarTitle;
Modal.Header = StyledHeader;
Modal.Aside = ModalAside;
Modal.AsideScrollArea = ModalAsideScrollArea;
Modal.AsideFooter = StyledAsideFooter;
Modal.LeftAsideRoot = ModalLeftAsideRoot;
Modal.RightAsideRoot = ModalRightAsideRoot;
Modal.Title = ModalTitle;
Modal.Description = StyledDescription;
Modal.Section = StyledSection;
Modal.ScrollArea = ModalScrollArea;
Modal.ScrollArea_Legacy = ModalScrollArea_Legacy;
Modal.Fog = ModalFog;
Modal.Footer = Footer;
Modal.Footer_Legacy = StyledFooter_Legacy;
Modal.Body = StyledBody;
Modal.PortalContainer = ModalPortalContainer;
Modal.useModalContentElement = useModalContentElement;
Modal.useModalFooterElement = useModalFooterElement;

export { Modal };
//# sourceMappingURL=Modal.mjs.map
